import React, {
    createContext,
    useContext,
    ReactNode,
    useCallback,
    useMemo,
    useRef
} from 'react';
import type {AssistantPageContext} from '../api/types';
import {
    mergeContextLayers,
    type ContextLayer,
    type ContextLayers,
    type ContextLayerValue
} from '../contextLayers';

export type {ContextLayer, ContextLayers, ContextLayerValue} from '../contextLayers';

interface AssistantContextType {
    /** Publishes a layer; null clears it. */
    publish: (layer: ContextLayer, value: ContextLayerValue | null) => void;
    /** The merged context to send with the next message; null when nothing is published. */
    getContext: () => AssistantPageContext | null;
}

const AssistantContext = createContext<AssistantContextType | undefined>(
    undefined
);

export const AssistantContextProvider: React.FC<{children: ReactNode}> = ({
    children
}) => {
    // The layers live in a ref, not state: nothing renders from them (pages
    // publish; the panel reads them only when a message is sent), and this
    // provider sits above <Routes>, so holding them in state would re-render
    // the whole app every time a page republished (each dashboard week nav).
    const layersRef = useRef<ContextLayers>({});

    const publish = useCallback(
        (layer: ContextLayer, value: ContextLayerValue | null) => {
            const next = {...layersRef.current};
            if (value === null) {
                delete next[layer];
            } else {
                next[layer] = value;
            }
            layersRef.current = next;
        },
        []
    );

    const getContext = useCallback(
        () => mergeContextLayers(layersRef.current),
        []
    );

    // Every member is identity-stable, so this value never changes and the
    // provider never forces a subtree re-render.
    const contextValue = useMemo(
        () => ({publish, getContext}),
        [publish, getContext]
    );

    return (
        <AssistantContext.Provider value={contextValue}>
            {children}
        </AssistantContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAssistantContext = () => {
    const context = useContext(AssistantContext);
    if (context === undefined) {
        throw new Error(
            'useAssistantContext must be used within an AssistantContextProvider'
        );
    }
    return context;
};
